/****************************************************************************
 * Simple in-memory request cache with TTL and in-flight de-duplication.
 * Keeps redundant GET calls from hitting the backend.
 ****************************************************************************/

const DEFAULT_CACHE_TTL = 2 * 60 * 1000; // 2 minutes

class RequestCache {
  constructor() {
    this.cache = new Map();
    this.pending = new Map();
  }

  get(key) {
    const entry = this.cache.get(key);
    if (!entry) return null;

    const isExpired = Date.now() - entry.timestamp > entry.ttl;
    if (isExpired) {
      this.delete(key);
      return null;
    }

    return entry.value;
  }

  set(key, value, ttl = DEFAULT_CACHE_TTL) {
    this.cache.set(key, {
      value,
      timestamp: Date.now(),
      ttl,
    });
  }

  delete(key) {
    this.cache.delete(key);
    this.pending.delete(key);
  }

  clear(pattern) {
    if (!pattern) {
      this.cache.clear();
      this.pending.clear();
      return;
    }

    [...this.cache.keys()].forEach((key) => {
      if (pattern.test(key)) {
        this.delete(key);
      }
    });
  }

  getOrFetch(key, fetcher, ttl = DEFAULT_CACHE_TTL) {
    const cached = this.get(key);
    if (cached !== null && cached !== undefined) return Promise.resolve(cached);

    if (this.pending.has(key)) return this.pending.get(key);

    const fetchPromise = Promise.resolve()
      .then(fetcher)
      .then((data) => {
        this.set(key, data, ttl);
        this.pending.delete(key);
        return data;
      })
      .catch((err) => {
        this.pending.delete(key);
        throw err;
      });

    this.pending.set(key, fetchPromise);
    return fetchPromise;
  }
}

export { DEFAULT_CACHE_TTL, RequestCache };
export const requestCache = new RequestCache();
export default requestCache;
